import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Loader2, MapPin, Layers, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Geography() {
  const [communes, setCommunes] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [communeForm, setCommuneForm] = useState({ open: false, edit: null, name: "", active: true });
  const [zoneForm, setZoneForm] = useState({ open: false, edit: null, name: "", commune_ids: [], delivery_fee: 0, active: true });

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/communes"), api.get("/zones")]).then(([c, z]) => { setCommunes(c.data); setZones(z.data); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const communeName = (id) => communes.find((c) => c.id === id)?.name || "?";

  const saveCommune = async () => {
    try {
      const body = { name: communeForm.name, active: communeForm.active };
      if (communeForm.edit) await api.put(`/communes/${communeForm.edit}`, body);
      else await api.post("/communes", body);
      toast.success("Commune enregistrée"); setCommuneForm({ open: false, edit: null, name: "", active: true }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delCommune = async (id) => {
    try { await api.delete(`/communes/${id}`); toast.success("Supprimée"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveZone = async () => {
    try {
      const body = { name: zoneForm.name, commune_ids: zoneForm.commune_ids, delivery_fee: Number(zoneForm.delivery_fee) || 0, active: zoneForm.active };
      if (zoneForm.edit) await api.put(`/zones/${zoneForm.edit}`, body);
      else await api.post("/zones", body);
      toast.success("Zone enregistrée"); setZoneForm({ open: false, edit: null, name: "", commune_ids: [], delivery_fee: 0, active: true }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delZone = async (id) => { await api.delete(`/zones/${id}`); toast.success("Supprimée"); load(); };

  const toggleCommuneInZone = (id) => setZoneForm((f) => ({ ...f, commune_ids: f.commune_ids.includes(id) ? f.commune_ids.filter((x) => x !== id) : [...f.commune_ids, id] }));

  if (loading) return <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>;

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Géographie</h1><p className="text-slate-500 text-sm mt-1">Communes et zones de livraison</p></div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Communes */}
        <div className="bg-white border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><MapPin className="w-5 h-5 text-brand-600" /> Communes</h3>
            <Button data-testid="commune-new-btn" size="sm" onClick={() => setCommuneForm({ open: true, edit: null, name: "", active: true })} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          <div className="space-y-2">
            {communes.map((c) => (
              <div key={c.id} data-testid={`commune-row-${c.id}`} className="flex items-center justify-between border rounded-xl px-3 py-2">
                <span className="font-medium">{c.name}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs rounded-full px-2 py-0.5 ${c.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Active" : "Inactive"}</span>
                  <button onClick={() => setCommuneForm({ open: true, edit: c.id, name: c.name, active: c.active })} className="p-1 text-slate-400 hover:text-brand-600"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`commune-del-${c.id}`} onClick={() => delCommune(c.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Zones */}
        <div className="bg-white border rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><Layers className="w-5 h-5 text-brand-600" /> Zones de livraison</h3>
            <Button data-testid="zone-new-btn" size="sm" onClick={() => setZoneForm({ open: true, edit: null, name: "", commune_ids: [], delivery_fee: 0, active: true })} className="bg-brand-600 hover:bg-brand-700"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
          <div className="space-y-2">
            {zones.map((z) => (
              <div key={z.id} data-testid={`zone-row-${z.id}`} className="border rounded-xl px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{z.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-brand-700">{money(z.delivery_fee)}</span>
                    <button onClick={() => setZoneForm({ open: true, edit: z.id, name: z.name, commune_ids: z.commune_ids || [], delivery_fee: z.delivery_fee, active: z.active })} className="p-1 text-slate-400 hover:text-brand-600"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`zone-del-${z.id}`} onClick={() => delZone(z.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="text-xs text-slate-400 mt-1">{(z.commune_ids || []).map(communeName).join(", ") || "Aucune commune"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Commune dialog */}
      <Dialog open={communeForm.open} onOpenChange={(v) => setCommuneForm((f) => ({ ...f, open: v }))}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{communeForm.edit ? "Modifier" : "Nouvelle"} commune</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input data-testid="commune-name" value={communeForm.name} onChange={(e) => setCommuneForm({ ...communeForm, name: e.target.value })} className="mt-1" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={communeForm.active} onCheckedChange={(v) => setCommuneForm({ ...communeForm, active: v })} /> Active</label>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setCommuneForm((f) => ({ ...f, open: false }))}>Annuler</Button><Button data-testid="commune-save" onClick={saveCommune} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>

      {/* Zone dialog */}
      <Dialog open={zoneForm.open} onOpenChange={(v) => setZoneForm((f) => ({ ...f, open: v }))}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{zoneForm.edit ? "Modifier" : "Nouvelle"} zone</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nom de la zone</Label><Input data-testid="zone-name" value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Frais livraison ($)</Label><Input data-testid="zone-fee" type="number" value={zoneForm.delivery_fee} onChange={(e) => setZoneForm({ ...zoneForm, delivery_fee: e.target.value })} className="mt-1" /></div>
            </div>
            <div>
              <Label>Communes associées</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                {communes.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm border rounded-lg px-2 py-1.5 cursor-pointer">
                    <Checkbox checked={zoneForm.commune_ids.includes(c.id)} onCheckedChange={() => toggleCommuneInZone(c.id)} data-testid={`zone-commune-${c.id}`} /> {c.name}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={zoneForm.active} onCheckedChange={(v) => setZoneForm({ ...zoneForm, active: v })} /> Active</label>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setZoneForm((f) => ({ ...f, open: false }))}>Annuler</Button><Button data-testid="zone-save" onClick={saveZone} className="bg-brand-600 hover:bg-brand-700">Enregistrer</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
